import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

export const MicroInsightScreen = ({ insight, overline, onContinue, autoAdvanceMs = 0 }) => {
  useEffect(() => {
    if (!autoAdvanceMs) return;
    const t = setTimeout(onContinue, autoAdvanceMs);
    return () => clearTimeout(t);
  }, [autoAdvanceMs, onContinue]);

  return (
    <motion.section
      className="relative min-h-screen w-full bg-brand-forest flex items-center justify-center px-5 sm:px-6 py-12 sm:py-16 overflow-hidden grain"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="insight-screen"
    >
      {/* ambient glows in brand greens */}
      <div
        className="pointer-events-none absolute -top-32 left-1/4 h-[320px] sm:h-[420px] w-[320px] sm:w-[420px] rounded-full blur-3xl opacity-25"
        style={{ background: 'radial-gradient(circle at center, #D8E5C0 0%, rgba(216,229,192,0) 70%)' }}
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 h-[240px] sm:h-[320px] w-[240px] sm:w-[320px] rounded-full blur-3xl opacity-20"
        style={{ background: 'radial-gradient(circle at center, #364634 0%, rgba(54,70,52,0) 70%)' }}
      />

      <div className="relative w-full max-w-3xl">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="flex items-center gap-3"
        >
          <div className="h-px w-8 sm:w-10 bg-brand-sage" />
          <span className="text-[10px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] font-semibold text-brand-sage font-heading" data-testid="insight-overline">
            Scientific Insight · {overline}
          </span>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.7 }}
          className="mt-6 sm:mt-8 font-heading font-light text-2xl sm:text-4xl lg:text-5xl text-white leading-[1.25] sm:leading-[1.18] tracking-tight"
          data-testid="insight-text"
        >
          {insight}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="mt-8 sm:mt-12"
        >
          <button
            onClick={onContinue}
            className="group inline-flex items-center justify-center gap-3 text-white border border-white/30 hover:border-white hover:bg-white/5 rounded-xl px-6 sm:px-7 py-3.5 text-sm font-heading font-semibold uppercase tracking-wider transition-all w-full sm:w-auto"
            data-testid="insight-continue-btn"
          >
            Continue
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.div>
      </div>
    </motion.section>
  );
};
